import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { postYardiGuestInviteValidate, postYardiManualInvite } from '../services/yardiInviteService'
import { validateData } from '../utils/yardiInviteFormatters'
import {
  getGuestCardPrimaryApplicant,
  guestCardProspectKey,
} from '../utils/yardiGuestCardGrouping'
import { canInviteGuestCard } from '../utils/yardiGuestCardStatus'
import type {
  YardiGuestCardApplication,
  YardiGuestCardRow,
  YardiGuestInviteValidateRequest,
  YardiGuestInviteValidateResponse,
} from '../types'
import { showError, showSuccess, formatApiError } from '@/lib/utils/notifications'
import { htmlOrTextToPlain } from '@/lib/utils/htmlPlainText'
import { ROUTE_PATHS } from '@/config/routes'

function formatGuestInviteError(error: unknown): string {
  const ax = error as { response?: { data?: unknown } }
  const data = ax?.response?.data
  if (typeof data === 'string') return htmlOrTextToPlain(data)
  if (data && typeof data === 'object' && data !== null && 'error' in data) {
    const errVal = (data as { error: unknown }).error
    if (typeof errVal === 'string') return htmlOrTextToPlain(errVal)
  }
  return htmlOrTextToPlain(formatApiError(error))
}

export function useYardiGuestInvite(opts: {
  companyId: string
  propertyId: string
  onInviteSent: (prospectIds: string[]) => void
}) {
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(false)
  const [anchorApplication, setAnchorApplication] = useState<YardiGuestCardApplication | null>(null)
  const [occupants, setOccupants] = useState<YardiGuestCardApplication['applicants']>([])
  const [selectedProspectIds, setSelectedProspectIds] = useState<string[]>([])
  const [validating, setValidating] = useState(false)
  const [validationResult, setValidationResult] = useState<YardiGuestInviteValidateResponse | null>(null)
  const [sending, setSending] = useState(false)

  const canSend = validationResult?.valid === true

  const runValidate = useCallback(
    async (application: YardiGuestCardApplication) => {
      if (!opts.companyId || !opts.propertyId) {
        showError('Company and property are required to validate.', false)
        return
      }

      const yardiPrimary = getGuestCardPrimaryApplicant(application.applicants)
      const prospectId = yardiPrimary.prospectId
      if (!prospectId) {
        showError('Prospect ID is required to validate.', false)
        return
      }

      setValidating(true)
      setValidationResult(null)
      setOccupants([])
      try {
        const body = validateData({
          company_id: opts.companyId,
          property_id: opts.propertyId,
          prospect_id: prospectId,
        }) as YardiGuestInviteValidateRequest
        const result = await postYardiGuestInviteValidate(body)
        setValidationResult(result)
        const validatedProspectIds = result.applicants
          .filter((applicant) => applicant.responsibleForLease !== false)
          .map((applicant) => applicant.prospectId.toUpperCase())
          .filter(Boolean)
        setSelectedProspectIds((previous) =>
          previous.filter((prospectId) => validatedProspectIds.includes(prospectId)),
        )
        setOccupants(
          result.applicants.map(
            (applicant): YardiGuestCardRow => ({
              prospectId: applicant.prospectId,
              tenantId: applicant.tenantId,
              firstName: applicant.firstName,
              middleName: applicant.middleName,
              lastName: applicant.lastName,
              email: applicant.email,
              mobileNo: applicant.mobileNo,
              birthDate: applicant.birthDate,
              creditCheckStatus: applicant.creditCheckStatus,
              type: applicant.relationshipType,
              responsibleForLease: applicant.responsibleForLease,
            }),
          ),
        )
      } catch (error: unknown) {
        showError(formatGuestInviteError(error), false)
        setValidationResult(null)
      } finally {
        setValidating(false)
      }
    },
    [opts.companyId, opts.propertyId],
  )

  const openInviteModal = useCallback(
    (application: YardiGuestCardApplication) => {
      const grouped = application.applicants
      const selectable = grouped.filter((o) => canInviteGuestCard(o))
      if (selectable.length === 0) return
      const selectedIds = selectable.map((o) => guestCardProspectKey(o)).filter(Boolean)
      setAnchorApplication(application)
      setOccupants([])
      setSelectedProspectIds(selectedIds)
      setValidationResult(null)
      setModalOpen(true)
      void runValidate(application)
    },
    [runValidate],
  )

  const closeInviteModal = useCallback(() => {
    if (sending || validating) return
    setModalOpen(false)
    setAnchorApplication(null)
    setOccupants([])
    setSelectedProspectIds([])
    setValidationResult(null)
  }, [sending, validating])

  const toggleOccupant = useCallback((prospectId: string) => {
    const key = prospectId.toUpperCase()
    setSelectedProspectIds((prev) =>
      prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key],
    )
  }, [])

  const revalidate = useCallback(() => {
    if (!anchorApplication || validating || sending) return
    void runValidate(anchorApplication)
  }, [anchorApplication, validating, sending, runValidate])

  const sendInvite = useCallback(async () => {
    if (!anchorApplication || selectedProspectIds.length === 0 || sending || !canSend) return
    if (!opts.companyId || !opts.propertyId) {
      showError('Company and property are required to send an invite.', false)
      return
    }

    const yardiPrimary = getGuestCardPrimaryApplicant(occupants)

    const prospectId = yardiPrimary.prospectId
    if (!prospectId) {
      showError('Prospect ID is required to send an invite.', false)
      return
    }

    setSending(true)
    try {
      const body = validateData({
        company_id: opts.companyId,
        property_id: opts.propertyId,
        prospect_id: prospectId,
        selected_prospect_ids: selectedProspectIds,
      }) as Record<string, unknown>
      await postYardiManualInvite(body)

      const sentProspectIds = [...selectedProspectIds]
      opts.onInviteSent(sentProspectIds)
      setModalOpen(false)
      setAnchorApplication(null)
      setOccupants([])
      setSelectedProspectIds([])
      setValidationResult(null)
      showSuccess('Invite process successfully')
      navigate(ROUTE_PATHS.DASHBOARD_YARDI_INVITE)
    } catch (error: unknown) {
      showError(formatGuestInviteError(error), false)
    } finally {
      setSending(false)
    }
  }, [
    anchorApplication,
    selectedProspectIds,
    sending,
    canSend,
    opts,
    occupants,
    navigate,
  ])

  return {
    modalOpen,
    occupants,
    selectedProspectIds,
    validating,
    validationResult,
    canSend,
    sending,
    openInviteModal,
    closeInviteModal,
    toggleOccupant,
    revalidate,
    sendInvite,
  }
}
